import { Injectable, inject } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { StockTransfer } from '../models/location.model';
import { NewStockMovement, StockMovement } from '../models/stock-movement.model';
import { Product, ProductVariant, StockByLocation } from '../models/product-v2.model';
import { CollectionPath, NewDoc, TenantDb, TenantTxn } from '../data/tenant-collection';

/**
 * Extended StockTransfer with additional fields for shipment and receipt tracking
 */
export interface ExtendedStockTransfer extends StockTransfer {
  shippedBy?: string;
  shippedByName?: string;
  shippedAt?: Date;
  receivedBy?: string;
  receivedByName?: string;
  receivedAt?: Date;
  receivedQuantity?: number;
  cancelledBy?: string;
  cancelledByName?: string;
  cancellationReason?: string;
  /** Physical location within the source warehouse/store (e.g., aisle, shelf) */
  physicalLocation?: string;
}

/**
 * Input for creating a new stock transfer
 */
export interface NewStockTransfer {
  businessId: string;
  fromLocationId: string;
  fromLocationName: string;
  fromLocationType: 'warehouse' | 'store';
  toLocationId: string;
  toLocationName: string;
  toLocationType: 'warehouse' | 'store';
  productId: string;
  productName: string;
  productReference: string;
  variantId?: string;
  variantName?: string;
  variantSku?: string;
  quantity: number;
  requestedBy: string;
  requestedByName: string;
  notes?: string;
  reason?: string;
  physicalLocation?: string; // Physical location within the warehouse/store (e.g., aisle, shelf)
}

/**
 * Stock Transfer Service
 * Manages stock transfer operations between locations.
 *
 * All Firestore access goes through the TenantCollection seam — tenant scoping, injection
 * context, audit fields, and the Timestamp<->Date codec live there, not here. Stock-moving
 * steps (ship/receive/cancel) adjust two locations' inventory plus an immutable movement log
 * inside a single `db.runTransaction` so quantity math stays atomic.
 */
@Injectable({
  providedIn: 'root',
})
export class StockTransferService {
  /** The transfer documents themselves (mutable lifecycle: pending -> in-transit -> completed/cancelled). */
  private transfers = inject(TenantDb).collection<ExtendedStockTransfer>('stock_transfers');
  /** The seam, for atomic multi-doc stock moves and the immutable movement log. */
  private db = inject(TenantDb);

  // Event emitter for when a transfer is received (used to notify POS to refresh)
  private transferReceivedSubject = new Subject<{ transferId: string; locationId: string }>();
  transferReceived$ = this.transferReceivedSubject.asObservable();

  /**
   * Create a new transfer request
   * Initial status is 'pending'
   */
  async createTransfer(transfer: NewStockTransfer): Promise<string> {
    return this.transfers.add({
      ...transfer,
      status: 'pending',
    } as NewDoc<ExtendedStockTransfer>);
  }

  /**
   * Get transfers by status
   */
  async getTransfersByStatus(status: StockTransfer['status']): Promise<ExtendedStockTransfer[]> {
    return this.transfers.list({
      where: [['status', '==', status]],
      orderBy: { field: 'createdAt', direction: 'desc' },
    });
  }

  /**
   * Get all transfers with optional status filter
   */
  async getTransfers(statuses?: StockTransfer['status'][]): Promise<ExtendedStockTransfer[]> {
    if (statuses && statuses.length > 0) {
      return this.transfers.list({
        where: [['status', 'in', statuses]],
        orderBy: { field: 'createdAt', direction: 'desc' },
      });
    }
    return this.transfers.list({ orderBy: { field: 'createdAt', direction: 'desc' } });
  }

  /**
   * Get pending transfers for a location (both incoming and outgoing)
   */
  async getPendingTransfersForLocation(locationId: string): Promise<ExtendedStockTransfer[]> {
    const [incoming, outgoing] = await Promise.all([
      this.transfers.list({
        where: [
          ['toLocationId', '==', locationId],
          ['status', 'in', ['pending', 'in-transit']],
        ],
        orderBy: { field: 'createdAt', direction: 'desc' },
      }),
      this.transfers.list({
        where: [
          ['fromLocationId', '==', locationId],
          ['status', 'in', ['pending', 'in-transit']],
        ],
        orderBy: { field: 'createdAt', direction: 'desc' },
      }),
    ]);

    // Combine and sort by createdAt
    return [...incoming, ...outgoing].sort(
      (a, b) => (b.createdAt?.getTime() || 0) - (a.createdAt?.getTime() || 0)
    );
  }

  /**
   * Get a single transfer by ID
   */
  async getTransferById(transferId: string): Promise<ExtendedStockTransfer | null> {
    return (await this.transfers.get(transferId)) ?? null;
  }

  /**
   * Approve a pending transfer
   */
  async approveTransfer(
    transferId: string,
    approvedBy: string,
    approvedByName: string
  ): Promise<void> {
    return this.transfers.update(transferId, {
      approvedBy,
      approvedByName,
      approvedAt: new Date(),
    } as Partial<NewDoc<ExtendedStockTransfer>>);
  }

  /**
   * Ship a transfer (mark as in-transit and deduct stock from source).
   * Atomic: reads source stock, writes the transfer + decremented stock + an outgoing
   * movement in one transaction.
   */
  async shipTransfer(transferId: string, shippedBy: string, shippedByName: string): Promise<void> {
    await this.db.runTransaction(async txn => {
      const transfer = await this.readTransferInTxn(txn, transferId);

      if (transfer.status !== 'pending') {
        throw new Error('Transfer must be in pending status to ship');
      }

      // READS FIRST (Firestore: all reads before all writes)
      const stock = await this.readStockForAdjust(txn, transfer);

      // 1. Update transfer status
      txn.update<ExtendedStockTransfer>('stock_transfers', transferId, {
        status: 'in-transit',
        shippedBy,
        shippedByName,
        shippedAt: new Date(),
      } as Partial<NewDoc<ExtendedStockTransfer>>);

      // 2. Deduct stock from source location (read-then-write replaces FieldValue.increment)
      this.applyStockAdjust(txn, stock, transfer.fromLocationId, -transfer.quantity);

      // 3. Create outgoing stock movement (immutable audit log)
      const outMovement: Partial<NewStockMovement> = {
        businessId: transfer.businessId,
        productId: transfer.productId,
        productName: transfer.productName,
        variantId: transfer.variantId,
        variantName: transfer.variantName,
        variantSku: transfer.variantSku || transfer.productReference,
        type: 'transfer',
        status: 'completed',
        quantity: transfer.quantity,
        direction: 'out',
        locationId: transfer.fromLocationId,
        locationName: transfer.fromLocationName,
        fromLocationId: transfer.fromLocationId,
        fromLocationName: transfer.fromLocationName,
        toLocationId: transfer.toLocationId,
        toLocationName: transfer.toLocationName,
        transferId,
        reconciliationStatus: 'pending',
        referenceType: 'transfer',
        referenceId: transferId,
        reason: `Transferencia a ${transfer.toLocationName}`,
        employeeId: shippedBy,
        employeeName: shippedByName,
        movementDate: new Date(),
      };

      txn.add<StockMovement>('stock_movements', outMovement as NewDoc<StockMovement>);
    });
  }

  /**
   * Receive a transfer (complete and add stock to destination).
   * Atomic: reads destination stock, writes the transfer + incremented stock + an incoming
   * movement in one transaction. Mismatch is tracked on the transfer and the incoming movement.
   */
  async receiveTransfer(
    transferId: string,
    receivedQuantity: number,
    receivedBy: string,
    receivedByName: string,
    notes?: string
  ): Promise<void> {
    let toLocationId = '';

    await this.db.runTransaction(async txn => {
      const transfer = await this.readTransferInTxn(txn, transferId);

      if (transfer.status !== 'in-transit') {
        throw new Error('Transfer must be in-transit to receive');
      }

      toLocationId = transfer.toLocationId;

      // Determine reconciliation status
      const reconciliationStatus =
        receivedQuantity === transfer.quantity ? 'reconciled' : 'mismatch';

      // READS FIRST (Firestore: all reads before all writes)
      const stock = await this.readStockForAdjust(txn, transfer);

      // 1. Update transfer status
      const transferUpdate: Record<string, unknown> = {
        status: 'completed',
        receivedBy,
        receivedByName,
        receivedAt: new Date(),
        receivedQuantity,
        reconciliationStatus, // Track mismatch status on the transfer itself
        completedAt: new Date(),
        completedBy: receivedBy,
        completedByName: receivedByName,
      };

      if (notes) {
        transferUpdate['notes'] = transfer.notes
          ? `${transfer.notes}\n---\nNotas de recepción: ${notes}`
          : `Notas de recepción: ${notes}`;
      }

      txn.update<ExtendedStockTransfer>(
        'stock_transfers',
        transferId,
        transferUpdate as Partial<NewDoc<ExtendedStockTransfer>>
      );

      // 2. Add stock to destination location
      this.applyStockAdjust(txn, stock, transfer.toLocationId, receivedQuantity);

      // 3. Create incoming stock movement (immutable audit log)
      const inMovement: Partial<NewStockMovement> = {
        businessId: transfer.businessId,
        productId: transfer.productId,
        productName: transfer.productName,
        variantId: transfer.variantId,
        variantName: transfer.variantName,
        variantSku: transfer.variantSku || transfer.productReference,
        type: 'transfer',
        status: 'completed',
        quantity: receivedQuantity,
        direction: 'in',
        locationId: transfer.toLocationId,
        locationName: transfer.toLocationName,
        fromLocationId: transfer.fromLocationId,
        fromLocationName: transfer.fromLocationName,
        toLocationId: transfer.toLocationId,
        toLocationName: transfer.toLocationName,
        transferId,
        reconciliationStatus,
        quantityReceived: receivedQuantity,
        reconciledAt: new Date(),
        reconciledBy: receivedBy,
        reconciledByName: receivedByName,
        referenceType: 'transfer',
        referenceId: transferId,
        reason: `Transferencia desde ${transfer.fromLocationName}`,
        notes,
        employeeId: receivedBy,
        employeeName: receivedByName,
        movementDate: new Date(),
      };

      // Note: We don't update the outgoing movement because stock_movements are immutable (audit
      // trail). The reconciliation status is tracked on the transfer document and the incoming
      // movement. The transfer document has receivedQuantity vs quantity to determine mismatch.
      txn.add<StockMovement>('stock_movements', inMovement as NewDoc<StockMovement>);
    });

    // Emit event to notify listeners (e.g., POS) that a transfer was received
    this.transferReceivedSubject.next({ transferId, locationId: toLocationId });
  }

  /**
   * Cancel a transfer.
   * Atomic: if the transfer was in-transit, restores stock to the source location and writes a
   * reversal movement alongside the status change in one transaction.
   */
  async cancelTransfer(
    transferId: string,
    cancelledBy: string,
    cancelledByName: string,
    reason: string
  ): Promise<void> {
    await this.db.runTransaction(async txn => {
      const transfer = await this.readTransferInTxn(txn, transferId);

      if (transfer.status === 'completed') {
        throw new Error('Cannot cancel a completed transfer');
      }

      const wasInTransit = transfer.status === 'in-transit';

      // READS FIRST (Firestore: all reads before all writes) — only needed if restoring stock
      const stock = wasInTransit ? await this.readStockForAdjust(txn, transfer) : null;

      // 1. Update transfer status
      txn.update<ExtendedStockTransfer>('stock_transfers', transferId, {
        status: 'cancelled',
        cancelledBy,
        cancelledByName,
        cancelledAt: new Date(),
        cancellationReason: reason,
      } as Partial<NewDoc<ExtendedStockTransfer>>);

      // 2. If transfer was in-transit, restore stock to source location
      if (wasInTransit && stock) {
        this.applyStockAdjust(txn, stock, transfer.fromLocationId, transfer.quantity);

        // Create a reversal stock movement (immutable audit log)
        const reversalMovement: Partial<NewStockMovement> = {
          businessId: transfer.businessId,
          productId: transfer.productId,
          productName: transfer.productName,
          variantId: transfer.variantId,
          variantName: transfer.variantName,
          variantSku: transfer.variantSku || transfer.productReference,
          type: 'adjustment',
          status: 'completed',
          quantity: transfer.quantity,
          direction: 'in',
          locationId: transfer.fromLocationId,
          locationName: transfer.fromLocationName,
          referenceType: 'transfer',
          referenceId: transferId,
          reason: `Transferencia cancelada: ${reason}`,
          employeeId: cancelledBy,
          employeeName: cancelledByName,
          movementDate: new Date(),
        };

        txn.add<StockMovement>('stock_movements', reversalMovement as NewDoc<StockMovement>);
      }
    });
  }

  /**
   * Reject a pending transfer (similar to cancel but for pending status)
   */
  async rejectTransfer(
    transferId: string,
    rejectedBy: string,
    rejectedByName: string,
    reason: string
  ): Promise<void> {
    return this.cancelTransfer(transferId, rejectedBy, rejectedByName, reason);
  }

  /**
   * Live read of all transfers (most recent 100), newest first.
   * Replaces the manual onSnapshot listener — RxJS subscription lifecycle (e.g. takeUntil)
   * now owns teardown, so {@link unsubscribeFromAllTransfers} is a no-op.
   */
  subscribeToAllTransfers(): Observable<ExtendedStockTransfer[]> {
    return this.transfers.observe({
      orderBy: { field: 'createdAt', direction: 'desc' },
      limit: 100,
    });
  }

  /**
   * @deprecated Listener teardown is now owned by the observe() subscription (unsubscribe the
   * Observable instead, e.g. via takeUntil). Kept as a no-op for backward compatibility.
   */
  unsubscribeFromAllTransfers(): void {
    // No-op: observe() has no manual listener to detach.
  }

  // ============================================================================
  // PRIVATE HELPERS
  // ============================================================================

  /** Read a transfer inside a transaction, throwing if it doesn't exist. */
  private async readTransferInTxn(
    txn: TenantTxn,
    transferId: string
  ): Promise<ExtendedStockTransfer> {
    const transfer = await txn.get<ExtendedStockTransfer>('stock_transfers', transferId);
    if (!transfer) {
      throw new Error('Transfer not found');
    }
    return transfer;
  }

  /**
   * READ half of a stock adjustment. Must run before any transaction write (Firestore
   * requires all reads before all writes). Returns the doc location + current stock map.
   */
  private async readStockForAdjust(
    txn: TenantTxn,
    transfer: ExtendedStockTransfer
  ): Promise<StockAdjustRead> {
    const path: CollectionPath = transfer.variantId
      ? ['products', transfer.productId, 'variants']
      : ['products'];
    const docId = transfer.variantId ?? transfer.productId;
    const current = await txn.get<Product | ProductVariant>(path, docId);
    return {
      path,
      docId,
      stockByLocation: { ...(current?.stockByLocation ?? {}) },
    };
  }

  /**
   * WRITE half: apply `delta` to a location and write the stock map back. The atomic,
   * seam-friendly replacement for `FieldValue.increment`. Must run after all reads.
   */
  private applyStockAdjust(
    txn: TenantTxn,
    read: StockAdjustRead,
    locationId: string,
    delta: number
  ): void {
    const stockByLocation = read.stockByLocation;
    const existing = stockByLocation[locationId];
    stockByLocation[locationId] = {
      ...existing,
      quantity: (existing?.quantity ?? 0) + delta,
      lastUpdated: new Date(),
    };
    txn.update<Product | ProductVariant>(read.path, read.docId, { stockByLocation } as Partial<
      NewDoc<Product | ProductVariant>
    >);
  }
}

interface StockAdjustRead {
  path: CollectionPath;
  docId: string;
  stockByLocation: { [locationId: string]: StockByLocation };
}
